import { Card, cn } from '@ai-tutor/ui';

import { BrandPanel } from '@/components/core/layout/BrandPanel';

/**
 * The shell every auth route renders into: brand on the left, task on the
 * right.
 *
 * Replaces the four hand-copied `flex min-h-screen items-center justify-center
 * pt-16` + `w-full max-w-lg px-4` shells that used to live in `AuthPage`,
 * `ResetPasswordPage`, `AccountRestorePage` and `AuthCallbackPage` - which had
 * already drifted (AuthCallbackPage put `max-w-lg` on the Card itself, so the
 * width guard in `authShellWidth.test.ts` only ever covered three of them).
 *
 * Three details that look like arbitrary values and are not:
 *
 * - **`min-h-[100dvh]`, not a fixed height.** With a fixed `min-h`, the grid is
 *   only as tall as that value and `items-center` centres the content inside a
 *   block that itself sits at the top of a taller viewport. `dvh` rather than
 *   `vh` so the iOS Safari address bar cannot cause a jump; `min-h` rather than
 *   `h` so a tall form on a short screen grows and scrolls instead of clipping.
 * - **`md:justify-center`.** The columns cap at 20rem and 30rem, so with the
 *   `lg:gap-x-20` they total 880px and never fill the 64rem (1024px) container.
 *   Without this the
 *   grid packs them to the start and parks ~200px of dead space on the right,
 *   putting the pair 100px left of page centre at every viewport. Scoped to
 *   `md:` because below the breakpoint the single column should still stretch -
 *   a bare `justify-center` would shrink the card to its content width.
 * - **No `BrandBar`.** `BrandPanel` carries the wordmark; rendering both shows
 *   it twice.
 * - **`content-center`.** Below `md` the grid has TWO rows (brand, form) in a
 *   `min-h-[100dvh]` box, and grid rows stretch by default: the spare height
 *   was split between them, so a short form (reset, verifying, expired link)
 *   sat centred in the bottom half with ~260px of nothing above it. This packs
 *   both rows at content height and centres them as one block. From `md` there
 *   is one row, which it centres exactly as `items-center` did.
 * - **`py-6 gap-6` below `md`, `py-14 gap-10` from it.** On a phone the brand
 *   stacks above the form, so desktop spacing pushed sign-in and register 109px
 *   past a 390x844 screen. The phone values (with `BrandPanel`'s compact
 *   headline and `AuthCard`'s `p-5`) are what keep them on one screen.
 */
export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className='mx-auto grid min-h-[100dvh] max-w-[64rem] content-center items-center gap-6 px-6 py-6 md:grid-cols-[minmax(0,20rem)_minmax(0,30rem)] md:justify-center md:gap-10 md:py-14 lg:gap-x-20'>
      <BrandPanel />
      {children}
    </div>
  );
}

/**
 * The right-hand surface.
 *
 * `elevation='md'` rather than a `shadow-elevation-2` class override. The
 * reasoning that used to live here — stock `shadow` is a black shadow, which is
 * invisible against a near-black dark ground — now lives on the `Card`
 * primitive, where it applies to every card rather than to this one. Passing it
 * as a variant also keeps `cn` out of it: `shadow-elevation-*` are custom keys,
 * and relying on tailwind-merge to pick a winner between two of them is a
 * guess, where a variant is a decision.
 */
export function AuthCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card elevation='md' className={cn('w-full p-5 md:p-7', className)}>
      {children}
    </Card>
  );
}
